import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import {resetUserDetail} from "app/store/slices/userSlice";
import {
reSetCredentials, setHeaderServiceFailure, setShowGlobalAlert,
toggleAuthModal,
toggleCartDrawer,
setLogoutRequested,
setForceLogoutErrorMsg,
setHideNavigation,
} from "app/store/slices/authSlice";
import {resetOrder} from "app/store/slices/orderSlice";
import {resetStore} from "app/store/slices/storeSlice";
import {resetLocation} from "app/store/slices/locationSlice";
import {resetSelectedReward} from "app/store/slices/rewardsSlice";
import {RootState} from "app/store";
import { OT_QUERY_PARAMS } from "app/shared/constants/constants";
import { useLogoutMutation } from "app/services/api/mutateGateAPI";
import logging from "app/shared/utils/logging";
import { getEventNamespace } from "app/shared/utils/AzureNamespace";
import { getErrorMessage } from "app/shared/constants/ErrorMessages";
import { resetTOrder} from "store/slices/tOrderSlice";

const useAppUtil = () => {
    type LogoutOptions = {
        clearAuthTokenCookies?: boolean;
        showLoginModal?: boolean;
    };
        const appInsights = useAppInsightsContext();
        if (appInsights) {
            appInsights.trackPageView({
                name: "useAppUtil",
            });
            const metricData = {
                average: 5,
                name: "React Component Engaged Time (seconds)",
                sampleCount: 1,
            };
            const additionalProperties = { "Component Name": 'useAppUtil' };
            appInsights.trackMetric(metricData, additionalProperties);
        }

    const dispatch = useDispatch();
    const navigate = useNavigate();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [logout] = useLogoutMutation();
    const {isGuest, user} = useSelector((state: RootState) => state.auth);
    const scrollToTop = () => {
        window.scrollTo(0, 0);
        document.body.scrollTop = 0;
    };
    const navigateHome = () => {
        dispatch(setHeaderServiceFailure(false));
        navigate("/");
    };
    const excludeOTParams = () => {
        const excludeKeys = new Set(OT_QUERY_PARAMS);
        Object.keys(localStorage).forEach((key) => {
        if (!excludeKeys.has(key)) {
            localStorage.removeItem(key);
        }
        });
    };

    const logoutHandler = ({
        clearAuthTokenCookies = true,
        showLoginModal = false,
    }: LogoutOptions = {}) => {
        window.sessionStorage.clear();
        if (clearAuthTokenCookies) {
            try {
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                logout();
                logging.info(
                    appInsights,
                    getEventNamespace("AUTHENTICATION", "LOGOUT"),
                    "Logout successful cleared cookies",
                    user,
                );
            } catch (error) {
                logging.error(
                    appInsights,
                    getEventNamespace("AUTHENTICATION", "LOGOUT"),
                    `unable to clear cookies ${user ?? ""}`,
                    error,
                );
            }
        }
        excludeOTParams();
        dispatch(reSetCredentials());
        dispatch(resetUserDetail());
        dispatch(resetOrder());
        dispatch(resetTOrder());
        dispatch(resetStore());
        dispatch(resetLocation());
        dispatch(resetSelectedReward());
        dispatch(setShowGlobalAlert(true));
        dispatch(setLogoutRequested(false));

        const pages = ["passwordreset", "passwordReset", "forgotpassword", "forgotPassword", "signup"];
        if (!pages.some((data) => window.location.href.includes(data))) {
            navigate("/");
        }
        if (showLoginModal) {
            dispatch(setForceLogoutErrorMsg(getErrorMessage("SIGNUP", "forceLogoutMsg")));
            dispatch(setHideNavigation(false));
            dispatch(toggleCartDrawer(false));
            dispatch(toggleAuthModal(true));
        }
    };
    const resetApp = (redirectTo = "/", clearStore = true) => {
        window.sessionStorage.clear();
        if (isGuest) {
            excludeOTParams();
            dispatch(reSetCredentials());
            dispatch(resetUserDetail());
        }
        if (clearStore) {
            dispatch(resetStore());
            dispatch(resetLocation());
        }
        dispatch(resetOrder());
        dispatch(resetSelectedReward());
        navigate(redirectTo);
    };

    const disableBrowserBackNavigation = (scoped = false) => {
        // Use scoped mode when a page needs to remove its Back button handler later.
        if (scoped) {
            const { href } = window.location;
            const { state } = window.history as { state: unknown };
            let restoring = false;
            const handlePopState = (event: PopStateEvent) => {
                // Stop other Back button handlers from leaving this page.
                event.stopImmediatePropagation();
                if (restoring) {
                    // Moving forward also fires this event. Ignore it to avoid a loop.
                    restoring = false;
                    return;
                }
                restoring = true;
                // Undo the Back action by moving forward to this page again.
                window.history.go(1);
            };
            // Add a copy of this page to history and keep its router data.
            window.history.pushState(state, document.title, href);
            // The final true makes this handler run before the app's other handlers.
            window.addEventListener("popstate", handlePopState, true);
            // Let the page remove this handler when it closes.
            return () => window.removeEventListener("popstate", handlePopState, true);
        }
        window.history.pushState(null, document.title, window.location.href);
        window.onpopstate = () => {
            window.history.go(1);
        };
        return undefined;
    };

    return {
        scrollToTop,
        resetApp,
        disableBrowserBackNavigation,
        logoutHandler,
        navigateHome,
    };
};
export default useAppUtil;
